// RunUIScene — parallel HUD launched alongside RunScene. Subscribes to
// `pocket:changed` on the RunScene event bus, re-renders the
// PocketStrip widget, and updates the live "best 5-card hand so far"
// label with a flash on rank improvement (D-12).
//
// Lifecycle invariants:
//   - The bound listener reference is stored in `boundOnPocketChanged`
//     so the SHUTDOWN cleanup `off()` removes the EXACT same function
//     reference (Phaser's `off()` requires identity equality on the
//     handler — a fresh arrow inside `off()` would silently leak).
//   - `lastRank = -1` resets when the pocket falls below 5 cards. Going
//     back above 5 will then flash again on the FIRST evaluation —
//     intended (the player just rebuilt a 5-of-7).
//
// Ring 2 (full Phaser scene). Imports `bestOfPocket` from Ring 0
// `handEval.ts` and `PocketStrip` from Ring 2 ui.

import Phaser from 'phaser';
import { TestSeam } from '../debug/TestSeam';
import { PocketStrip } from '../ui/PocketStrip';
import { bestOfPocket } from '../poker/handEval';
import type { Card } from '../run/types';

interface UIInitData {
  runScene?: Phaser.Scene;
}

const HAND_LABEL_X = 6;
const HAND_LABEL_Y = 30;
const HAND_LABEL_COLOR_DEFAULT = '#f5e1c8';
const HAND_LABEL_COLOR_FLASH = '#f5c542';
const FLASH_DURATION_MS = 150;

export class RunUIScene extends Phaser.Scene {
  private runScene: Phaser.Scene | null = null;
  private strip!: PocketStrip;
  private bestHandLabel!: Phaser.GameObjects.Text;
  private lastRank = -1;
  private boundOnPocketChanged: ((p: { pocket: Card[] }) => void) | null = null;

  constructor() {
    super('RunUIScene');
  }

  init(data: UIInitData): void {
    this.runScene = data.runScene ?? null;
  }

  create(): void {
    TestSeam.setScene('RunUIScene');

    this.strip = new PocketStrip(this);
    this.bestHandLabel = this.add
      .text(HAND_LABEL_X, HAND_LABEL_Y, '—', {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: HAND_LABEL_COLOR_DEFAULT,
      })
      .setOrigin(0, 0);

    if (!this.runScene) return;
    const events = this.runScene.events;

    this.boundOnPocketChanged = ({ pocket }) => this.onPocketChanged(pocket);
    events.on('pocket:changed', this.boundOnPocketChanged, this);

    // Shared Pattern 4 — listener cleanup. SHUTDOWN fires when the UI
    // scene stops (e.g. RunScene restart triggered by __TEST__.reset).
    // Without this `off()` Phaser would re-add the listener on the next
    // create() while the old one stays bound — every reset would double
    // (then triple, then quadruple) the listener count.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (this.boundOnPocketChanged) {
        events.off('pocket:changed', this.boundOnPocketChanged, this);
      }
      this.boundOnPocketChanged = null;
    });

    this.strip.render([]);
  }

  private onPocketChanged(pocket: Card[]): void {
    this.strip.render(pocket);
    const result = bestOfPocket(pocket);
    if (!result) {
      this.bestHandLabel.setText('—');
      this.lastRank = -1;
      return;
    }
    // bestOfPocket returns the human-readable name in `descr`: pokersolver
    // reports a royal flush as name='Straight Flush' descr='Royal Flush'
    // (handEval.test.ts documents the quirk). The HUD must show the
    // descr so the royal renders correctly — and descr is strictly more
    // informative for other hands ("Two Pair, K's & 7's" vs "Two Pair").
    // Plan 04 success criterion #4 ("label shows 'Royal Flush'") locks
    // this in. The plan's <interfaces> example (`result.name`) was
    // pre-Plan-02-SUMMARY guidance that survives in this comment as a
    // pointer to the resolved decision.
    this.bestHandLabel.setText(result.descr);
    if (result.rank > this.lastRank) this.flashBestHandLabel();
    this.lastRank = result.rank;
  }

  private flashBestHandLabel(): void {
    this.bestHandLabel.setColor(HAND_LABEL_COLOR_FLASH);
    this.time.delayedCall(FLASH_DURATION_MS, () => {
      this.bestHandLabel.setColor(HAND_LABEL_COLOR_DEFAULT);
    });
  }
}
